import React, { useState, useEffect, useCallback } from 'react';
import { getSms, refreshSmsDelivery } from '../services/smsService';
import TableListFooter from '../components/TableListFooter';
import SmsDurumIkonlari from '../components/SmsDurumIkonlari';
import SmsNetgsmDetayModal from '../components/SmsNetgsmDetayModal';

const formatDate = (value) => {
  if (!value) return '-';
  try {
    let date;
    if (typeof value === 'string' || typeof value === 'number') {
      date = new Date(value);
    } else if (value instanceof Date) {
      date = value;
    } else {
      return '-';
    }

    if (isNaN(date.getTime())) return '-';

    return date.toLocaleString('tr-TR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
  } catch (error) {
    console.error('Date format error:', error, value);
    return '-';
  }
};

const getSmsTipBadge = (tip) => {
  const tipMap = {
    servis_kaydi_acildi: { text: 'Servis Kaydı Açıldı', color: '#3b82f6', bg: '#dbeafe' },
    randevu_olusturuldu: { text: 'Randevu Oluşturuldu', color: '#10b981', bg: '#d1fae5' },
    randevu_iptal_edildi: { text: 'Randevu İptal Edildi', color: '#ef4444', bg: '#fee2e2' },
    otp: { text: 'OTP Doğrulama', color: '#8b5cf6', bg: '#ede9fe' },
    servis_kaydi_randevu_detay: { text: 'Servis Detay Raporu', color: '#0d9488', bg: '#ccfbf1' },
    on_servis_kaydi_otp: { text: 'Ön Servis Kaydı', color: '#d97706', bg: '#fef3c7' },
    musteri_iletisim_izni_otp: { text: 'İletişim İzni OTP', color: '#6366f1', bg: '#eef2ff' },
    sifre_sifirlama_otp: { text: 'Şifre Sıfırlama', color: '#0f766e', bg: '#ccfbf1' },
  };

  const tipInfo = tipMap[tip] || { text: tip, color: '#64748b', bg: '#f1f5f9' };

  return (
    <span style={{
      padding: '4px 12px',
      borderRadius: '12px',
      fontSize: '12px',
      fontWeight: 600,
      backgroundColor: tipInfo.bg,
      color: tipInfo.color,
    }}
    >
      {tipInfo.text}
    </span>
  );
};

const SmsManagementPage = () => {
  const [smsler, setSmsler] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({
    mevcutSayfa: 1,
    toplamSayfa: 1,
    toplam: 0,
    limit: 50,
  });
  const [detaySms, setDetaySms] = useState(null);
  const [refreshingId, setRefreshingId] = useState(null);

  const loadSms = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await getSms({
        sayfa: currentPage,
        limit: 50,
      });

      if (response.basarili) {
        setSmsler(response.veri.smsler || []);
        setPagination(response.veri.sayfalama || {
          mevcutSayfa: 1,
          toplamSayfa: 1,
          toplam: 0,
          limit: 50,
        });
      } else {
        setError(response.mesaj || 'SMS\'ler yüklenirken bir hata oluştu');
      }
    } catch (err) {
      console.error('SMS yükleme hatası:', err);
      if (err?.status === 404) {
        setError('API endpoint bulunamadı. Backend endpoint\'inin doğru yapılandırıldığından emin olun.');
      } else if (err?.data?.mesaj) {
        setError(err.data.mesaj);
      } else {
        setError(err.message || 'SMS\'ler yüklenirken bir hata oluştu');
      }
    } finally {
      setLoading(false);
    }
  }, [currentPage]);

  useEffect(() => {
    loadSms();
  }, [loadSms]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.toplamSayfa) {
      setCurrentPage(newPage);
    }
  };

  const handleRefreshDelivery = async (sms) => {
    const id = sms?.id || sms?._id;
    if (!id) return;
    setRefreshingId(id);
    try {
      const response = await refreshSmsDelivery(id);
      if (response?.veri?.sms) {
        setSmsler((prev) => prev.map((row) => (
          (row.id || row._id) === id ? response.veri.sms : row
        )));
        if (detaySms && (detaySms.id || detaySms._id) === id) {
          setDetaySms(response.veri.sms);
        }
      }
    } catch (err) {
      console.error('Teslim yenileme hatası:', err);
    } finally {
      setRefreshingId(null);
    }
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>SMS Yönetimi</h1>
      </div>

      {error && (
        <div style={{
          padding: '12px 16px',
          backgroundColor: '#fee2e2',
          color: '#991b1b',
          borderRadius: '8px',
          marginBottom: '24px',
        }}
        >
          {error}
        </div>
      )}

      <div className="form-page">
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <div className="loading-spinner" />
            <p style={{ marginTop: '16px' }}>Yükleniyor...</p>
          </div>
        ) : (
          <>
            {smsler.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <i className="fas fa-sms" style={{ fontSize: '48px', color: '#d1d5db', marginBottom: '16px' }} />
                <p style={{ color: '#6b7280' }}>Henüz SMS gönderilmemiş.</p>
              </div>
            ) : (
              <>
                <div style={{ overflowX: 'auto' }}>
                  <table className="admins-table">
                    <thead>
                      <tr>
                        <th>Telefon</th>
                        <th>Mesaj</th>
                        <th>Tip</th>
                        <th>Durum</th>
                        <th>Gönderilme Zamanı</th>
                      </tr>
                    </thead>
                    <tbody>
                      {smsler.map((sms) => (
                        <tr key={sms.id || sms._id}>
                          <td style={{ fontFamily: 'monospace', fontWeight: 500 }}>
                            {sms.telefon || '-'}
                          </td>
                          <td style={{ maxWidth: '400px', wordWrap: 'break-word' }}>
                            <div style={{
                              padding: '8px 12px',
                              backgroundColor: '#f8fafc',
                              borderRadius: '6px',
                              fontSize: '13px',
                              lineHeight: '1.5',
                              whiteSpace: 'pre-wrap',
                            }}
                            >
                              {sms.mesaj || '-'}
                            </div>
                          </td>
                          <td>
                            {getSmsTipBadge(sms.tip)}
                          </td>
                          <td>
                            <SmsDurumIkonlari
                              sms={sms}
                              onInfoClick={setDetaySms}
                              onRefreshClick={handleRefreshDelivery}
                              refreshing={refreshingId === (sms.id || sms._id)}
                            />
                          </td>
                          <td>
                            {formatDate(sms.olusturmaTarihi)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <TableListFooter
                  totalRecords={pagination.toplam}
                  currentPage={pagination.mevcutSayfa || currentPage}
                  totalPages={pagination.toplamSayfa || 1}
                  onPageChange={handlePageChange}
                  loading={loading}
                  embedded
                />
              </>
            )}
          </>
        )}
      </div>

      {detaySms && (
        <SmsNetgsmDetayModal
          sms={detaySms}
          onClose={() => setDetaySms(null)}
        />
      )}
    </div>
  );
};

export default SmsManagementPage;
